import React from "react";
import { Link } from "react-router-dom";
import { IdentityView, ScanCard } from "../../services/physicalIdentityApi";
import { HKPImage } from "../../media/HKPImage";
import ScanTimeline, { scanDay } from "./ScanTimeline";
import ScanStageStrip from "./ScanStageStrip";
import { rememberReturnTo } from "../sales/internalQr";
import { kp } from "../karkhana/karkhanaPaths";
import { useKT } from "../karkhana/i18n/kt";

const inr = (raw?: string | null) => {
  const n = Number(String(raw ?? "").replace(/[,₹\s]/g, ""));
  if (raw == null || raw === "" || !Number.isFinite(n)) return "";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: n % 1 ? 2 : 0 }).format(n);
};

/**
 * One scanned saree. The studio that made it gets a quick card (price and status for staff, production
 * only for karkhana supervisors) with its stage and one next action, and the journey; everyone else sees
 * that it is being made, or the certificate once it is published.
 */
export default function ProductScanView({ view, token, onChanged, children }: { view: IdentityView; token: string; onChanged?: () => void; children?: React.ReactNode }) {
  const { t } = useKT();
  const certificate = view.links?.certificate;
  if (!view.card) return <PublicView view={view} token={token} certificate={certificate} />;
  const card = view.card;
  const staff = view.viewerRole === "MAKER_OWNER";
  const weaving = view.timeline?.weaving;
  return <div className="scan-layout">
    <section className="scan-card" aria-label={t("Product")}>
      <Photo card={card} />
      <div className="scan-card__body">
        <p className="scan-eyebrow">{staff ? "Your studio’s piece" : t("In your karkhana")}</p>
        <h1 className="scan-title">{card.name || "Unnamed product"}</h1>
        <p className="scan-meta">{[card.designNumber && `Design ${card.designNumber}`, card.sareeNumber && `Saree ${card.sareeNumber}`, card.loomCode && `Loom ${card.loomCode}`, card.colour].filter(Boolean).join(" · ")}</p>
        <p className="scan-id">{card.productUniqueId}</p>

        {staff && <dl className="scan-prices">
          <div className="scan-price is-sale"><dt>Sale price</dt><dd>{inr(card.salePrice) || <span className="scan-missing">Not set</span>}</dd></div>
          {"costPrice" in card && <div className="scan-price"><dt>Cost price</dt><dd>{inr(card.costPrice) || <span className="scan-missing">Not set</span>}</dd></div>}
        </dl>}

        <div className="scan-chips">
          {staff && card.commerce && <Commerce commerce={card.commerce} />}
          <span className={`scan-chip${card.certificatePublished ? " is-ok" : ""}`}>{card.certificatePublished ? t("Certificate published") : t("Certificate not published yet")}</span>
        </div>

        <ScanStageStrip view={view} token={token} onMoved={() => onChanged?.()} showEdit={staff} />

        <div className="scan-actions">
          {certificate && <Link className="scan-btn" to={certificate}>{t("View certificate")}</Link>}
          {staff && view.links?.product && <Link className="scan-btn" to={view.links.product}>Open product</Link>}
        </div>
      </div>
    </section>

    <section className="scan-journey" aria-labelledby="scan-journey-title">
      <div className="scan-journey__head">
        <h2 id="scan-journey-title">{t("Journey")}</h2>
        {weaving && weaving.status === "ACTIVE" && <p className="scan-meta">{weaving.wovenMetres} of {weaving.fullMetres} m woven · {weaving.daysOnLoom} {weaving.daysOnLoom === 1 ? "day" : "days"} on the loom</p>}
      </div>
      {weaving && weaving.waitingApproval > 0 && <p className="scan-note">{weaving.waitingApproval} {weaving.waitingApproval === 1 ? "entry is" : "entries are"} waiting for approval and not counted yet.</p>}
      {view.timeline && <ScanTimeline timeline={view.timeline} />}
      {children}
    </section>
  </div>;
}

function Photo({ card }: { card: ScanCard }) {
  if (card.image) return <div className="scan-photo"><HKPImage preset="PRODUCT_CARD" legacyUrl={card.image} alt={card.name || "Product"} loading="eager" /></div>;
  return <div className="scan-photo is-empty" aria-hidden="true"><span>{(card.sareeNumber || card.name || "HKP").slice(0, 6)}</span></div>;
}

function Commerce({ commerce }: { commerce: NonNullable<ScanCard["commerce"]> }) {
  if (commerce.state === "SOLD") return <span className="scan-chip is-sold">Sold{commerce.soldTo ? ` to ${commerce.soldTo}` : ""}{commerce.invoiceNumber ? ` · ${commerce.invoiceNumber}` : ""}</span>;
  if (commerce.state === "ON_MEMO") return <span className="scan-chip is-held">On memo{commerce.memoCode ? ` ${commerce.memoCode}` : ""}{commerce.partyName ? ` · ${commerce.partyName}` : ""}</span>;
  return <span className="scan-chip is-ok">In stock</span>;
}

/** Signed-out scanners who work at the studio: the owner's login or the supervisor's phone + PIN, both coming back to this label. */
export function SignInChoices({ token }: { token: string }) {
  const back = () => rememberReturnTo(`/q/${token}`);
  return <div className="scan-signin" aria-label="Sign in">
    <p className="scan-note">Work at this studio? Sign in to see its stage and next step.</p>
    <div className="scan-actions is-center">
      <Link className="scan-btn" to="/login" onClick={back}>Studio owner / staff</Link>
      <Link className="scan-btn" to={kp.login()} onClick={back}>Karkhana supervisor</Link>
    </div>
  </div>;
}

/** Public, other studios and buyers: being made, then the certificate, then My Collection once it is theirs. A buying studio also gets its product page. */
function PublicView({ view, token, certificate }: { view: IdentityView; token: string; certificate?: string }) {
  // The server's answer, not localStorage: an expired login on a shared phone reads as signed out.
  const signedIn = view.viewerMode !== "PUBLIC";
  const transferred = view.state?.lifecycle === "CUSTOMER_COLLECTION";
  if (certificate) return <section className="scan-public">
    <p className="scan-eyebrow">HastKala Pramanak</p>
    <h1 className="scan-title">{view.title}</h1>
    {view.maker && <p className="scan-meta">Handwoven by {view.maker}</p>}
    <div className="scan-actions is-center">
      <Link className="scan-btn is-primary" to={certificate}>Show certificate</Link>
      {view.viewerRole === "BUYER_STAFF" && view.links?.product && <Link className="scan-btn" to={view.links.product}>Open product</Link>}
      {transferred && view.viewerRole !== "BUYER_STAFF" && view.links?.collection && <Link className="scan-btn" to={view.links.collection}>Add to My Collection</Link>}
    </div>
    {!transferred && view.links?.collection && <p className="scan-note">Bought this piece? After the certificate opens you can keep it in My Collection.</p>}
  </section>;
  return <section className="scan-public">
    <span className="scan-loom-mark" aria-hidden="true" />
    <p className="scan-eyebrow">HastKala Pramanak</p>
    <h1 className="scan-title">Still being made</h1>
    <p className="scan-lede">{view.maker ? <>Handwoven at <strong>{view.maker}</strong>. </> : null}This piece is on its way. Its certificate will appear here once it is ready.</p>
    {!signedIn && <SignInChoices token={token} />}
  </section>;
}

export { scanDay };
